import { useEffect, useState } from "react";
import {
  ArrowRight, Check, X, ChevronDown, Phone,
  DollarSign, TrendingUp, PieChart, Calculator,
  Users, Shield, BarChart3, MonitorSmartphone,
  Clock, BookOpen, ChevronRight
} from "lucide-react";
import ApplyModal from "./ApplyModal";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const G = "#C9A84C";   // gold
const BG   = "#111111";
const BGA  = "#161616";  // alt section bg
const CARD = "#1a1a1a";
const CARD2= "#1e1e1e";
const BR   = "rgba(255,255,255,0.07)";

const STATS = [
  { num: "10,000+", label: "Properties Supported" },
  { num: "50+",   label: "Firms Served" },
  { num: "60%",   label: "Cost Savings" },
  { num: "2 WKS", label: "To Deploy" },
];

const ROLES = [
  { icon: <DollarSign size={20} style={{color:G}}/>,        title: "Fund Accountant",     desc: "NAV/GAV calculations, LP/GP allocations, K-1 prep, waterfall distributions, investor reporting." },
  { icon: <Calculator size={20} style={{color:G}}/>,        title: "Property Accountant", desc: "Month-end close, bank reconciliations, CAM reconciliation, owner reporting, AP/AR management." },
  { icon: <TrendingUp size={20} style={{color:G}}/>,        title: "Underwriter",         desc: "Pro forma modeling, deal analysis, sensitivity analysis, investment committee memos." },
  { icon: <PieChart size={20} style={{color:G}}/>,          title: "Asset Manager",       desc: "NOI analysis, capital planning, multi-property consolidation, portfolio performance reporting." },
  { icon: <MonitorSmartphone size={20} style={{color:G}}/>, title: "IT Specialist",       desc: "Yardi / AppFolio administration, help desk, platform support, system health monitoring." },
  { icon: <Users size={20} style={{color:G}}/>,             title: "HR Specialist",       desc: "Full-cycle recruitment, onboarding, HR administration, talent sourcing and documentation." },
  { icon: <Shield size={20} style={{color:G}}/>,            title: "Legal & Compliance",  desc: "Lease administration, contract management, compliance documentation and regulatory tracking." },
  { icon: <BarChart3 size={20} style={{color:G}}/>,         title: "Business Support",    desc: "CRM management, Google Business Tools, digital operations, website audits, marketing ops." },
];

const STEPS = [
  { num: "01", title: "Book a Call",     desc: "15 minutes. Tell us exactly what your firm needs. No pitch, no obligation." },
  { num: "02", title: "We Find the Fit", desc: "We match you with pre-vetted, domain-trained professionals from our pool. Zero effort on your end." },
  { num: "03", title: "You Meet Them",   desc: "Interview and select. We handle contracts, onboarding, and system access." },
  { num: "04", title: "They're Yours",   desc: "Your dedicated team starts delivering from day one. No ramp time. No surprises." },
];

const CASE_STUDIES = [
  {
    category: "Finance & Accounting",
    title: "Property Audits Transformed for a Leading Real Estate Firm",
    outcome: "50% reduction in audit preparation time",
    description: "A 300+ property firm was drowning in year-end audit prep. X Analyst embedded a dedicated team that rebuilt the workflow from scratch.",
    fullContent: `A leading U.S. real estate management firm managing 300+ residential and commercial properties was spending excessive time on year-end property audits. Their internal team was overwhelmed with PBC list management, auditor liaison work, and financial statement preparation — leading to reporting delays and compliance risk.\n\nX Analyst embedded a team of 3 dedicated property accounting specialists with deep audit support experience. Within the first 60 days, the team:\n\n• Restructured the entire PBC checklist and documentation workflow\n• Implemented a rolling audit-prep calendar to eliminate year-end crunch\n• Reduced reconciliation turnaround from 5 days to 1 day\n• Created standardized workpaper templates reused across 50+ properties\n\nResult: 50% reduction in audit preparation time and zero compliance findings in the following year-end audit cycle.`,
  },
  {
    category: "Fund Accounting",
    title: "PE Real Estate Fund Accounting Infrastructure Built in 30 Days",
    outcome: "Full fund admin setup in 30 days — $180K saved",
    description: "A PE fund closing its second vehicle needed complete NAV, LP capital statements, and K-1 infrastructure — without hiring a full internal team.",
    fullContent: `A private equity real estate fund manager preparing to close their second fund ($85M target) needed full fund accounting infrastructure but couldn't justify the cost of a domestic team for the initial 12-18 months.\n\nX Analyst deployed a dedicated fund accounting specialist with experience in real estate PE fund structures. Within 30 days:\n\n• Set up complete fund accounting structure in their existing platform\n• Built NAV/GAV calculation models with quarterly review process\n• Created LP capital account tracking for 24 limited partners\n• Established K-1 preparation workflow ahead of first year-end\n• Built waterfall distribution models (American structure with preferred return)\n\nResult: Full fund accounting infrastructure operational in 30 days. First LP capital statements delivered on time. Fund manager avoided $180K/year in domestic CPA hiring costs.`,
  },
  {
    category: "Finance & Accounting",
    title: "$1.5M Saved Annually — US Property Management Company",
    outcome: "$1.5M saved annually",
    description: "A 200+ unit property management company replaced a $1.8M/year in-house finance function with a dedicated X Analyst team at a fraction of the cost.",
    fullContent: `A rapidly scaling U.S. property management company operating 200+ multifamily units across 3 states had built an in-house finance function that was costing over $1.8M/year in fully-loaded labor costs.\n\nX Analyst embedded 4 dedicated property accountants at $3,500/person/month. The transition was completed in 6 weeks with zero operational disruption. Within 12 months:\n\n• Annual finance function cost dropped from $1.8M to $168,000\n• Month-end close cycle improved by 3 days\n• Error rate on reconciliations dropped by 62%\n\nResult: $1.5M+ saved annually with improved output quality and faster close cycles.`,
  },
  {
    category: "IT Managed Services",
    title: "IT Support Desk for a Global Real Estate Leader",
    outcome: "99.9% platform uptime maintained",
    description: "A global real estate operator needed round-the-clock IT support. X Analyst's dedicated managed services team became their primary help desk.",
    fullContent: `A global real estate operator managing properties across 8 U.S. markets needed a reliable IT support function covering 6AM–10PM hours across Yardi, AppFolio, and MRI platforms.\n\nX Analyst embedded a team of 2 IT managed services professionals:\n\n• Primary help desk coverage for 450+ internal users\n• Platform administration for Yardi and AppFolio environments\n• 30-minute SLA for P1 incidents\n• Monthly system health reporting\n\nWithin 6 months:\n• First-call resolution rate: 87% (up from 54%)\n• Average resolution time: 2.1 hours (down from 6.8)\n• Zero critical outages in 18 months post-onboarding\n\nResult: 99.9% platform uptime maintained across all markets.`,
  },
  {
    category: "Legal & Compliance",
    title: "40% Reduction in Legal Processing Costs",
    outcome: "$112K in annual legal savings",
    description: "A property management firm spending $280K/year on contracts and lease admin embedded an X Analyst legal support specialist — and cut costs by 40%.",
    fullContent: `A mid-size property management company was spending $280K/year on legal and contract management. X Analyst embedded a dedicated legal support specialist with property management contract expertise.\n\nWithin the first 90 days:\n• Lease renewal turnaround: 14 days → 3 days\n• Standardized contract library built for 12 agreement types\n• External law firm dependency reduced by 70%\n• Compliance tracking calendar implemented\n\nResult: $112K in annual legal savings. Legal team now focused on complex litigation only.`,
  },
  {
    category: "HR & Recruitment",
    title: "60% Faster Time-to-Hire for Rapid Portfolio Expansion",
    outcome: "28 of 30 roles filled in 90 days",
    description: "A 40% YoY growth operator couldn't keep up with 30+ open positions. X Analyst's dedicated HR specialist filled almost every role in 3 months.",
    fullContent: `A large property management company experiencing 40% year-over-year portfolio growth needed to fill 30+ open positions across accounting, property management, and maintenance roles.\n\nX Analyst embedded a dedicated HR and recruitment specialist:\n• Full-cycle recruitment for all open roles\n• Standardized job descriptions and screening scorecards\n• Interview scheduling, reference checks, offer coordination\n• Complete onboarding documentation workflow\n\nWithin 90 days:\n• 28 of 30 roles filled\n• Time-to-hire: 47 days → 19 days (60% improvement)\n• Onboarding completion: 72% → 100%\n\nResult: Portfolio expansion staffed on time, zero delays to operations.`,
  },
];

const FAQS = [
  { q:"How quickly can we get started?", a:"Most clients have a dedicated team member onboarded and delivering within 2 weeks of their first call. We handle all setup, system access, and process documentation." },
  { q:"How many people can I hire?", a:"As many as you need. Each professional is dedicated entirely to your firm. Most start with one and scale up as they see results." },
  { q:"What platforms do your people work in?", a:"Our talent is experienced across Yardi, AppFolio, MRI, RealPage, Entrata, QuickBooks, and most major RE platforms. We match you with someone fluent in your stack." },
  { q:"What qualifications do your team members have?", a:"CPA/CA qualified with 5–12 years of real estate accounting, fund accounting, and finance experience. Every candidate passes our 7-step vetting process before placement." },
  { q:"How do you ensure data security?", a:"SOC 2 Type II compliant. Bank-level encryption, 2FA on all accounts, signed NDAs for every operator, continuous security monitoring." },
  { q:"Are there long-term contracts?", a:"No. Month-to-month. Cancel anytime. Most clients don't because their dedicated team delivers from week one." },
];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useFadeUp() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); }}),
      { threshold: 0.08 }
    );
    document.querySelectorAll(".fade-up").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

/* ─────────────────────────────────────────────
   DETAIL MODAL
───────────────────────────────────────────── */
function DetailModal({ item, onClose, onBook }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="detail-modal">
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{borderColor:BR}}>
          <span className="text-xs px-3 py-1 rounded-full" style={{background:"rgba(201,168,76,0.12)", color:G}}>{item.category}</span>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-2xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="px-6 py-6">
          <h2 className="text-lg font-bold text-white mb-3 leading-snug">{item.title}</h2>
          {item.outcome && (
            <div className="flex items-center gap-2 mb-4 px-4 py-2 rounded-lg border" style={{background:"rgba(201,168,76,0.06)", borderColor:"rgba(201,168,76,0.2)"}}>
              <Check size={14} style={{color:G}} className="flex-shrink-0"/>
              <span className="text-sm font-semibold" style={{color:G}}>{item.outcome}</span>
            </div>
          )}
          {item.readTime && (
            <div className="flex items-center gap-2 text-xs mb-4" style={{color:G}}>
              <Clock size={12}/><span>{item.readTime}</span>
            </div>
          )}
          <div className="text-sm leading-relaxed whitespace-pre-line" style={{color:"#999"}}>{item.fullContent}</div>
        </div>
        <div className="border-t px-6 py-4" style={{borderColor:BR}}>
          <button onClick={onBook} className="btn-gold w-full py-3 rounded-full justify-center text-sm">
            <Phone size={14}/>Book a 15-Minute Call
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────── */
function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="faq-item border rounded-xl overflow-hidden"
      data-open={open}
      style={{background:CARD, borderColor: open ? "rgba(201,168,76,0.3)" : BR}}
    >
      <button
        type="button"
        className="faq-trigger flex w-full items-center justify-between px-6 py-5 text-left text-white"
        onClick={() => setOpen(!open)}
        data-testid={`faq-${idx}`}
      >
        <span className="font-medium pr-4">{q}</span>
        <ChevronDown size={16} className="shrink-0 text-gray-500 transition-transform duration-200" style={{transform: open ? "rotate(180deg)" : "none"}}/>
      </button>
      {open && <div className="px-6 pb-5 text-sm leading-relaxed" style={{color:"#888"}}>{a}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */
export default function LandingPage({ onAdminClick }) {
  const [showApply, setShowApply]     = useState(false);
  const [selected, setSelected]       = useState(null);
  useFadeUp();

  const book = () => { setSelected(null); setShowApply(true); };

  return (
    <main style={{background:BG}}>
      {showApply  && <ApplyModal onClose={() => setShowApply(false)} />}
      {selected   && <DetailModal item={selected} onClose={() => setSelected(null)} onBook={book} />}

      {/* ══════════════════════════════════════
          NAV
      ══════════════════════════════════════ */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{background:"rgba(17,17,17,0.94)", backdropFilter:"blur(12px)", borderColor:BR}}
        data-testid="navbar"
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <button onClick={() => window.scrollTo({top:0,behavior:"smooth"})} className="flex items-center gap-2" style={{fontFamily:"'DM Sans',sans-serif"}} data-testid="brand-logo">
            <span style={{
              display:"inline-flex", alignItems:"center", justifyContent:"center",
              width:"30px", height:"30px",
              background:G, borderRadius:"6px",
              color:"#111111", fontWeight:"900", fontSize:"17px",
              fontFamily:"'Bebas Neue',sans-serif", lineHeight:1,
              flexShrink:0
            }}>X</span>
            <span className="font-bold text-xl tracking-tight text-white">Analyst</span>
          </button>
          <div className="hidden md:flex items-center gap-8">
            {[["services","Services"],["roles","What You Get"],["how-it-works","How It Works"],["pricing","Pricing"]].map(([id,label])=>(
              <button key={id} onClick={() => scrollTo(id)} className="text-sm transition-colors hover:text-white" style={{color:"#888"}}>{label}</button>
            ))}
            <button onClick={() => setShowApply(true)} className="btn-gold px-5 py-2 rounded-full text-sm" data-testid="nav-cta">
              <Phone size={13}/>Book a Call
            </button>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section
        id="hero"
        data-testid="hero-section"
        aria-label="Hero"
        className="flex items-start pt-14"
        style={{background:BG}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 w-full py-8 md:py-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <div className="fade-up">
              <div
                className="inline-block text-xs px-3 py-1 rounded-full mb-8 tracking-widest uppercase"
                style={{background:"rgba(201,168,76,0.1)", color:G, border:"1px solid rgba(201,168,76,0.2)"}}
              >
                Finance &amp; Accounting for Real Estate &amp; PE
              </div>

              <h1 className="bebas hero-h1 text-white mb-6">
                DEDICATED FINANCE<br/>
                &amp; ACCOUNTING.<br/>
                BUILT INTO<br/>
                <span style={{color:G}}>YOUR FIRM.</span>
              </h1>

              <p className="text-base md:text-lg mb-10 max-w-lg leading-relaxed" style={{color:"#888"}}>
                We embed senior-level fund accountants, underwriters, and asset managers directly into your operations.
                Not an agency. Not a vendor. <strong className="text-white">Yours.</strong>
              </p>

              <button onClick={() => setShowApply(true)} className="btn-gold px-8 py-4 rounded-full text-base" data-testid="hero-cta">
                <Phone size={16}/> Book a 15-Minute Call
              </button>
              <p className="mt-4 text-xs" style={{color:"#555"}}>No commitment. Deployed in 2 weeks.</p>
            </div>

            {/* RIGHT — floating dark card */}
            <div className="fade-up fade-up-d2 hidden lg:block">
              <div className="relative">
                {/* main card */}
                <div className="rounded-2xl p-6 border" style={{background:CARD, borderColor:BR}}>
                  <div className="text-xs uppercase tracking-widest mb-4" style={{color:"#555"}}>Currently Deployed</div>
                  <div className="space-y-3 mb-6">
                    {[
                      ["Priya S. — Fund Accountant",       "Active — Quarterly Reporting"],
                      ["Rahul M. — Underwriter",           "Active — Acquisition Model"],
                      ["Ananya K. — Property Accountant",  "Active — Month-End Close"],
                    ].map(([name, status]) => (
                      <div key={name} className="flex items-center justify-between rounded-lg px-4 py-3 border" style={{background:BGA, borderColor:BR}}>
                        <div>
                          <p className="text-sm font-medium text-white">{name}</p>
                          <p className="text-xs" style={{color:"#555"}}>{status}</p>
                        </div>
                        <div className="w-2 h-2 rounded-full" style={{background:G}}></div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4" style={{borderColor:BR}}>
                    <div className="flex items-center justify-between text-sm">
                      <span style={{color:"#555"}}>Annual cost (3 roles)</span>
                      <span className="font-bold" style={{color:G}}>$126,000</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span style={{color:"#555"}}>vs. domestic equivalent</span>
                      <span className="text-white line-through" style={{color:"#555"}}>$330,000</span>
                    </div>
                  </div>
                </div>
                {/* floating stat pill */}
                <div className="absolute -top-5 -right-5 rounded-xl px-4 py-3 border" style={{background:CARD2, borderColor:"rgba(201,168,76,0.25)"}}>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{color:"#555"}}>Cost Savings</p>
                  <p className="font-bold text-2xl bebas" style={{color:G}}>60%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════ */}
      <div
        data-testid="stats-strip"
        className="w-full border-y"
        style={{background:BGA, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {STATS.map((s, i) => (
              <div
                key={i}
                data-testid={`stat-${i}`}
                className="flex flex-col items-center justify-center py-8 px-4 text-center"
                style={{borderRight: i < 3 ? `1px solid ${BR}` : "none"}}
              >
                <p className="bebas text-5xl md:text-6xl" style={{color:G}}>{s.num}</p>
                <p className="text-xs uppercase tracking-widest mt-1" style={{color:"#555"}}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          WHAT YOU GET — ROLES
      ══════════════════════════════════════ */}
      <section
        id="roles"
        data-testid="roles-section"
        className="py-20 md:py-28"
        style={{background:BG}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="mb-14 fade-up">
            <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>01 WHAT YOU GET</p>
            <h2 className="bebas section-h text-white">
              EIGHT ROLES.<br/>
              <span style={{color:"#444"}}>ALL REAL ESTATE SPECIALISTS.</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed" style={{color:"#666"}}>
              Every professional we place has deep domain experience in real estate and PE. Not generalists repurposed — purpose-built for your industry.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ROLES.map((r, i) => (
              <article
                key={i}
                data-testid={`role-card-${i}`}
                className="role-card rounded-xl p-5 fade-up"
                style={{background:CARD, animationDelay:`${i*0.05}s`}}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{background:"rgba(201,168,76,0.08)"}}>
                  {r.icon}
                </div>
                <h3 className="font-semibold text-white text-sm mb-2">{r.title}</h3>
                <p className="text-xs leading-relaxed" style={{color:"#666"}}>{r.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          HOW IT WORKS — 4 STEPS
      ══════════════════════════════════════ */}
      <section
        id="how-it-works"
        data-testid="how-it-works-section"
        className="py-20 md:py-28 border-t"
        style={{background:BGA, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="mb-14 fade-up">
            <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>02 HOW IT WORKS</p>
            <h2 className="bebas section-h text-white">
              ONBOARD IN<br/>
              <span style={{color:G}}>2 WEEKS.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{background:BR}}>
            {STEPS.map((s, i) => (
              <div
                key={i}
                data-testid={`step-${i}`}
                className="step-card p-8 border"
                style={{background:CARD, borderColor:"transparent"}}
              >
                <p className="bebas text-6xl mb-6" style={{color:"rgba(201,168,76,0.2)"}}>{s.num}</p>
                <h3 className="font-bold text-white text-lg mb-3">{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{color:"#666"}}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          WHY AUXLO — COMPARISON TABLE
      ══════════════════════════════════════ */}
      <section
        id="services"
        data-testid="comparison-section"
        className="py-20 md:py-28 border-t"
        style={{background:BG, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="mb-14 fade-up">
            <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>03 WHY AUXLO</p>
            <h2 className="bebas section-h text-white">
              THE MODEL<br/>
              <span style={{color:"#444"}}>IS DIFFERENT.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 fade-up">
            {/* THEM */}
            <div className="col-them rounded-2xl p-8 border" style={{background:CARD, borderColor:BR}}>
              <p className="text-xs uppercase tracking-widest mb-6" style={{color:"#555"}}>Traditional Staffing Agency</p>
              <div className="mb-8">
                <p className="text-xs mb-1" style={{color:"#555"}}>Client Pays</p>
                <p className="bebas text-5xl text-white">$3,500</p>
              </div>
              <div className="space-y-3 pb-6 border-b" style={{borderColor:BR}}>
                {[["Agency cut","40%"],["Middlemen & management","30%"],["Overhead & operations","20%"]].map(([l,v])=>(
                  <div key={l} className="flex justify-between items-center text-sm">
                    <span style={{color:"#555"}}>{l}</span>
                    <span className="font-semibold text-red-400">{v}</span>
                  </div>
                ))}
              </div>
              <div className="pt-5 flex justify-between items-center">
                <span className="text-sm" style={{color:"#555"}}>Talent gets</span>
                <span className="bebas text-4xl text-red-400">10%</span>
              </div>
              <p className="text-xs mt-2" style={{color:"#444"}}>= $350/month. Pennies for actual work.</p>
              <div className="mt-6 space-y-2">
                {["High turnover, low motivation","Multiple layers of hierarchy","Generic output, not specialized"].map(t=>(
                  <div key={t} className="flex items-center gap-2 text-xs" style={{color:"#555"}}>
                    <X size={13} className="text-red-400 shrink-0"/>{t}
                  </div>
                ))}
              </div>
            </div>

            {/* YOU */}
            <div className="col-you rounded-2xl p-8 border" style={{background:CARD}}>
              <p className="text-xs uppercase tracking-widest mb-6" style={{color:G}}>The X Analyst Way</p>
              <div className="mb-8">
                <p className="text-xs mb-1" style={{color:"#666"}}>Client Pays</p>
                <p className="bebas text-5xl text-white">$3,500</p>
              </div>
              <div className="space-y-3 pb-6 border-b" style={{borderColor:BR}}>
                <div className="flex justify-between items-center text-sm">
                  <span style={{color:"#666"}}>Platform fee</span>
                  <span className="font-semibold" style={{color:G}}>20%</span>
                </div>
              </div>
              <div className="pt-5 flex justify-between items-center">
                <span className="text-sm" style={{color:"#666"}}>Talent gets</span>
                <span className="bebas text-4xl" style={{color:G}}>80%</span>
              </div>
              <p className="text-xs mt-2" style={{color:"#888"}}>= $2,800/month. <span style={{color:G}}>8× more than traditional.</span></p>
              <div className="mt-6 space-y-2">
                {["Talent owns outcomes — motivated to deliver","Zero middlemen between you and your team","Real estate specialists — not repurposed generalists"].map(t=>(
                  <div key={t} className="flex items-center gap-2 text-xs" style={{color:"#888"}}>
                    <Check size={13} className="shrink-0" style={{color:G}}/>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          ENGINEERED AI
      ══════════════════════════════════════ */}
      <section className="xa-ai border-t" style={{borderColor:"rgba(201,168,76,0.12)"}} data-testid="ai-section" aria-label="Engineered AI">
        <div className="xa-ai__wrap">

          <span className="xa-ai__eyebrow">Engineered AI · Not General AI</span>

          <h2 className="xa-ai__h2">
            Your workflow doesn't need <em>another chatbot.</em> It needs an engineer who builds the automation around it.
          </h2>

          <p className="xa-ai__lead">
            Every firm has tried it: drop the close into <strong>ChatGPT or Claude</strong> off the shelf and hope. It looks brilliant for three reconciliations — then it drifts, invents a number, and quietly breaks trust. <strong>You can't hand institutional capital to a tool that guesses.</strong> The same models, engineered properly, are a different story. Our engineers go inside your actual workflow, study how your close really runs, and design AI execution that holds — with your operator owning every exception and every judgment call.
          </p>

          {/* CONTRAST */}
          <div className="xa-ai__contrast">
            <div className="xa-ai__col xa-ai__col--bad">
              <div className="xa-ai__col-tag">The industry default</div>
              <h3 className="xa-ai__col-h">ChatGPT or Claude, off the shelf, pointed at a problem it was never configured for.</h3>
              <p className="xa-ai__col-p">
                A model trained on the whole internet, prompted to do property accounting. Confident for the first few results, then it hallucinates — and there's no engineer who knows why. Nobody owns the exceptions. Nobody owns the outcome. With LP money on the line, "usually right" isn't a standard.
              </p>
            </div>
            <div className="xa-ai__col xa-ai__col--good">
              <div className="xa-ai__col-tag">The X Analyst standard</div>
              <h3 className="xa-ai__col-h">The same models — engineered against your workflow by a dedicated specialist.</h3>
              <p className="xa-ai__col-p">
                An engineer builds AI execution against <em>your</em> CAM structure, your entities, your GL — constrained, tested, and battle-proven on your data. The system runs the repeatable work. Your operator handles the exceptions and the judgment. Every result traces to a human and a design decision.
              </p>
            </div>
          </div>

          {/* HOW ENGINEERS GET INSIDE THE WORKFLOW */}
          <h3 className="xa-ai__steps-h">How our engineers get inside your workflow</h3>
          <p className="xa-ai__steps-sub">Three steps from "AI we can't trust" to "automation we'd stake the close on."</p>

          <div className="xa-ai__steps">
            <div className="xa-ai__step">
              <div className="xa-ai__step-num">Step 01</div>
              <h4 className="xa-ai__step-h">Engineers map the real workflow</h4>
              <p className="xa-ai__step-p">A world-class engineer embeds alongside your operator and learns how the work actually moves — the edge cases, the system quirks, the decisions that never made it into an SOP. No generic template.</p>
            </div>
            <div className="xa-ai__step">
              <div className="xa-ai__step-num">Step 02</div>
              <h4 className="xa-ai__step-h">They design the AI execution</h4>
              <p className="xa-ai__step-p">We build specialised automation for your exact process — purpose-built, constrained, and tested against your data until it holds. Not ChatGPT or Claude as you'd use them off the shelf. The same class of models, engineered to stay accurate at volume.</p>
            </div>
            <div className="xa-ai__step">
              <div className="xa-ai__step-num">Step 03</div>
              <h4 className="xa-ai__step-h">Operators own the exceptions</h4>
              <p className="xa-ai__step-p">The AI runs the repeatable load. Your dedicated CA/CPA operator catches every exception and makes every judgment call — so accuracy stays a human responsibility, never a model's guess.</p>
            </div>
          </div>

          {/* PROOF */}
          <div className="xa-ai__proof">
            <div>
              <div className="xa-ai__stat-num">Highest</div>
              <div className="xa-ai__stat-label">success rate in the industry on engineered finance workflows</div>
            </div>
            <div className="xa-ai__proof-divider"></div>
            <div>
              <div className="xa-ai__stat-num">1:1</div>
              <div className="xa-ai__stat-label">a dedicated engineer designs each workflow — never a shared template</div>
            </div>
            <div className="xa-ai__proof-divider"></div>
            <div>
              <div className="xa-ai__stat-num">100%</div>
              <div className="xa-ai__stat-label">of exceptions and judgment calls owned by a human operator</div>
            </div>
          </div>

          {/* CTA */}
          <div className="xa-ai__cta-row">
            <p className="xa-ai__cta-text">See how an engineered workflow would run inside your firm.</p>
            <button className="xa-ai__btn" onClick={() => setShowApply(true)} data-testid="ai-section-cta">
              Book a 15-minute call
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 8h11M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════
          CASE STUDIES
      ══════════════════════════════════════ */}
      <section
        id="case-studies"
        data-testid="case-studies-section"
        className="py-20 md:py-28 border-t"
        style={{background:BGA, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="mb-14 fade-up">
            <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>04 CASE STUDIES</p>
            <h2 className="bebas section-h text-white">
              REAL RESULTS.<br/>
              <span style={{color:"#444"}}>REAL CLIENTS.</span>
            </h2>
            <p className="mt-4 text-sm" style={{color:"#555"}}>Click any card to read the full story.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {CASE_STUDIES.map((cs, i) => (
              <article
                key={i}
                data-testid={`case-study-${i}`}
                onClick={() => setSelected(cs)}
                onKeyDown={(e) => e.key === "Enter" && setSelected(cs)}
                tabIndex={0}
                role="button"
                aria-label={`Read: ${cs.title}`}
                className="cs-card rounded-xl p-6 border flex flex-col"
                style={{background:CARD, borderColor:BR}}
              >
                <span className="text-xs px-3 py-1 rounded-full w-fit mb-4" style={{background:"rgba(201,168,76,0.08)", color:G}}>{cs.category}</span>
                <h3 className="font-semibold text-white text-sm leading-snug mb-3 flex-1">{cs.title}</h3>
                <p className="text-xs leading-relaxed mb-5" style={{color:"#666"}}>{cs.description}</p>
                <div className="border-t pt-4 flex items-center justify-between" style={{borderColor:BR}}>
                  <div className="flex items-center gap-2">
                    <Check size={13} className="shrink-0" style={{color:G}}/>
                    <span className="text-xs font-semibold" style={{color:G}}>{cs.outcome}</span>
                  </div>
                  <span className="text-xs flex items-center gap-1" style={{color:"#444"}}>Read <ChevronRight size={11}/></span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          PRICING
      ══════════════════════════════════════ */}
      <section
        id="pricing"
        data-testid="pricing-section"
        className="py-20 md:py-28 border-t"
        style={{background:BG, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="mb-14 fade-up">
            <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>05 PRICING</p>
            <h2 className="bebas section-h text-white">
              ONE NUMBER.<br/>
              <span style={{color:"#444"}}>NO SURPRISES.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Price card */}
            <div className="price-card rounded-2xl p-8 md:p-12 fade-up" style={{background:CARD}}>
              <p className="text-xs uppercase tracking-widest mb-6" style={{color:G}}>Per Person / Per Month</p>
              <div className="flex items-end gap-3 mb-2">
                <span className="bebas text-8xl md:text-9xl text-white" style={{lineHeight:0.85}}>$3,500</span>
              </div>
              <p className="text-sm mb-10" style={{color:"#555"}}>All-inclusive. Billed monthly. Cancel anytime.</p>

              <div className="space-y-3 mb-10">
                {[
                  "Sourcing & placement from our vetted pool",
                  "Full onboarding & system integration",
                  "Dedicated success manager",
                  "Background checks, NDAs & compliance",
                  "Real estate & PE domain specialists only",
                  "U.S. hours coverage — no timezone friction",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm" style={{color:"#888"}}>
                    <Check size={15} className="shrink-0 mt-0.5" style={{color:G}}/>{item}
                  </div>
                ))}
              </div>

              <button onClick={() => setShowApply(true)} className="btn-gold w-full py-4 rounded-full text-base justify-center" data-testid="pricing-cta">
                <Phone size={15}/> Book a 15-Minute Call
              </button>
            </div>

            {/* Savings breakdown */}
            <div className="fade-up fade-up-d2 space-y-4">
              <div className="rounded-xl p-6 border" style={{background:CARD, borderColor:BR}}>
                <p className="text-xs uppercase tracking-widest mb-4" style={{color:"#555"}}>Annual cost comparison — 1 person</p>
                <div className="space-y-3">
                  {[
                    ["X Analyst",                       "$42,000/yr",  G,       false],
                    ["US-based equivalent hire",    "$100,000/yr", "#888",  true ],
                    ["Traditional staffing agency", "$84,000/yr",  "#888",  true ],
                  ].map(([label, cost, col, strike]) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span style={{color:"#666"}}>{label}</span>
                      <span className={`font-bold ${strike ? "line-through" : ""}`} style={{color:col}}>{cost}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl p-6 border" style={{background:CARD, borderColor:BR}}>
                <p className="text-xs uppercase tracking-widest mb-3" style={{color:"#555"}}>No long-term contracts</p>
                <div className="space-y-2">
                  {["Month-to-month billing","No setup or onboarding fees","Cancel anytime, no penalty"].map(t=>(
                    <div key={t} className="flex items-center gap-2 text-sm" style={{color:"#666"}}>
                      <Check size={14} className="shrink-0" style={{color:G}}/>{t}
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl p-6 border" style={{background:CARD, borderColor:BR}}>
                <p className="text-xs uppercase tracking-widest mb-3" style={{color:"#555"}}>Platforms we work in</p>
                <div className="flex flex-wrap gap-2">
                  {["Yardi","AppFolio","MRI","RealPage","QuickBooks","Entrata","Excel","Salesforce"].map(t=>(
                    <span key={t} className="text-xs px-3 py-1 rounded-full" style={{background:"rgba(255,255,255,0.04)", color:"#666"}}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FAQ
      ══════════════════════════════════════ */}
      <section
        id="faq"
        data-testid="faq-section"
        className="py-20 md:py-28 border-t"
        style={{background:BGA, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="fade-up">
              <p className="text-xs uppercase tracking-widest mb-4" style={{color:G}}>06 FAQ</p>
              <h2 className="bebas section-h text-white">
                QUESTIONS<br/>
                <span style={{color:"#444"}}>ANSWERED.</span>
              </h2>
              <p className="mt-6 text-sm max-w-sm leading-relaxed" style={{color:"#555"}}>
                Still unsure? Book a 15-minute call. We'll answer everything and show you exactly how it works.
              </p>
              <button onClick={() => setShowApply(true)} className="btn-gold px-6 py-3 rounded-full text-sm mt-8" data-testid="faq-cta">
                <Phone size={14}/>Book a Call
              </button>
            </div>
            <div className="space-y-3 fade-up fade-up-d2" data-testid="faq-list">
              {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i}/>)}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════ */}
      <section
        data-testid="footer-cta"
        className="py-24 md:py-36 border-t"
        style={{background:BG, borderColor:BR}}
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 text-center">
          <div className="fade-up">
            <h2 className="bebas text-white" style={{fontSize:"clamp(60px,11vw,160px)", lineHeight:0.88}}>
              BOOK A<br/>
              <span style={{color:G}}>15-MINUTE</span><br/>
              CALL.
            </h2>
            <p className="mt-8 mb-10 text-sm max-w-md mx-auto" style={{color:"#555"}}>
              Tell us what your firm needs. We'll tell you exactly how we can help — in 15 minutes flat.
            </p>
            <button
              onClick={() => setShowApply(true)}
              className="btn-gold px-10 py-4 rounded-full text-base"
              data-testid="footer-cta-button"
            >
              <Phone size={16}/> Book a 15-Minute Call
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOOTER
      ══════════════════════════════════════ */}
      <footer
        className="border-t py-8"
        style={{background:BGA, borderColor:BR}}
        data-testid="footer"
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="flex items-center gap-2">
            <span style={{
              display:"inline-flex", alignItems:"center", justifyContent:"center",
              width:"24px", height:"24px",
              background:G, borderRadius:"5px",
              color:"#111111", fontWeight:"900", fontSize:"14px",
              fontFamily:"'Bebas Neue',sans-serif", lineHeight:1,
              flexShrink:0
            }}>X</span>
            <span className="font-bold text-white">Analyst</span>
          </span>
          <nav aria-label="Footer" className="flex flex-wrap justify-center gap-6 text-sm" style={{color:"#444"}}>
            {[["services","Services"],["roles","What You Get"],["how-it-works","How It Works"],["case-studies","Case Studies"],["pricing","Pricing"],["faq","FAQ"]].map(([id,label])=>(
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-white transition-colors">{label}</button>
            ))}
            <button onClick={onAdminClick} data-testid="admin-link" className="hover:text-white transition-colors opacity-20">Admin</button>
          </nav>
          <span className="text-xs" style={{color:"#333"}}>© 2026 X Analyst</span>
        </div>
      </footer>
    </main>
  );
}
